import {
  DesignTokenSizeEnum,
  TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import { useTribalContext } from '@/context/tribal';
import FeatureCardsSection from '@/modules/features/compare/FeatureCardsSection/FeatureCardsSection';
import { formatCurrency } from '@/modules/utils/currency';
import { getCarOptionsData } from '@/modules/utils/storeUtils';
import useGlobalStore, {
  GlobalStoreStateConfiguration,
} from '@/stores/globalStore';
import { Box, SectionHeader } from '@/modules/components';
import { StyledKeyFeaturesSections } from './KeyFeatures.styles';
import TyreInformation from '@/modules/features/components/TyreInformation/TyreInformation';
import { FeatureStatus } from '@/hooks/useFeatures';
import { SectionNames } from '@/analytics/sectionNames';

const Features = () => {
  const { contentManager } = useTribalContext();
  const configurations = useGlobalStore((state) => state.app.configurations);

  const getPriceLabel = (option: any) => {
    // `getCarOptionsData` returns null when an option code cannot be resolved
    // (e.g. a stock vehicle with an empty `interior.upholstery` code), so guard
    // before destructuring to avoid crashing the whole Compare page.
    if (!option) return '';
    const { availability, price } = option;
    return availability === FeatureStatus.STANDARD_SPEC
      ? contentManager.get('standard-feature-label')
      : formatCurrency(price);
  };

  const buildSection = (
    title: string,
    getCode: (
      configuration: GlobalStoreStateConfiguration
    ) => string | undefined,
    getExtraProps?: (configuration: GlobalStoreStateConfiguration) => object
  ) => {
    if (!configurations.some((configuration) => getCode(configuration))) {
      return null;
    }

    return {
      title,
      cards: configurations.map((configuration) => {
        const code = getCode(configuration);
        if (!code) {
          return {
            body: contentManager.get('not-available-feature-label'),
            inactive: true,
          };
        }

        const carOptionsData = getCarOptionsData(configuration, code);

        return {
          ...carOptionsData,
          body: carOptionsData?.title,
          price: getPriceLabel(carOptionsData),
          decoratorImgContent: 'VW',
          ...getExtraProps?.(configuration),
        };
      }),
    };
  };

  const isSection = <T,>(section: T | null): section is T => section !== null;

  const exteriorSections = [
    buildSection(
      contentManager.get('exterior-option-paint-title'),
      (configuration) => configuration.pdbData?.exterior?.code
    ),
    buildSection(
      contentManager.get('exterior-option-wheels-title'),
      (configuration) => configuration.pdbData?.otherMonoselects?.wheels,
      (configuration) => ({
        secondaryLink: {
          label: contentManager.get('ctas-tyre-information'),
          modalContent: <TyreInformation configuration={configuration} />,
        },
      })
    ),
  ].filter(isSection);

  const interiorSections = [
    buildSection(
      contentManager.get('interior-option-upholstery-title'),
      (configuration) => {
        const interior = configuration.pdbData?.interior;
        return configuration.vin
          ? interior?.['upholstery-colour']
          : interior?.upholstery || interior?.['upholstery-colour'];
      }
    ),
    buildSection(
      contentManager.get('interior-option-in-car-entertainment-title'),
      (configuration) =>
        configuration.pdbData?.otherMonoselects?.['in-car-entertainment']
    ),
    buildSection(
      contentManager.get('interior-option-steering-wheel-title'),
      (configuration) =>
        configuration.pdbData?.otherMonoselects?.['steering-wheels']
    ),
  ].filter(isSection);

  return (
    <>
      <Box
        mb={DesignTokenSizeEnum.static370}
        b960={{ mb: DesignTokenSizeEnum.static530 }}
      >
        <SectionHeader
          title={contentManager.get('key-features-section-title-normal')}
          titleBold={contentManager.get('key-features-section-title-bold')}
          titleSize={TokenTextAppearance.headline400}
          description={contentManager.get('key-features-section-text')}
          descriptionSize={TokenTextAppearance.bigcopy100}
        />
      </Box>
      <StyledKeyFeaturesSections>
        {exteriorSections.length > 0 && (
          <FeatureCardsSection
            title={contentManager.get('your-exteriors-section-title')}
            sections={exteriorSections}
            sectionName={SectionNames.COMPARE_PAGE_EXTERIOR_FEATURES}
          />
        )}
        {interiorSections.length > 0 && (
          <FeatureCardsSection
            title={contentManager.get('your-interiors-section-title')}
            sections={interiorSections}
            sectionName={SectionNames.COMPARE_PAGE_INTERIOR_FEATURES}
          />
        )}
      </StyledKeyFeaturesSections>
    </>
  );
};

export default Features;
